import * as React from 'react';
import {
  type ThemeConfig,
  type ColorMode,
  type BaseColor,
  type Radius,
  type Style,
  type ThemeColor,
  type Font,
  THEME_STORAGE_KEY,
  THEME_PRESETS,
  RADIUS_VALUES,
  BASE_COLOR_PALETTES,
} from '@/lib/theme-presets';
import { DEFAULT_THEME } from '@/lib/default-theme';
import { getStyleById } from '@/lib/theme-styles';
import { getThemeColorById } from '@/lib/theme-colors';
import { loadFont, getFontById } from '@/lib/theme-fonts';

interface ThemeContextValue {
  theme: ThemeConfig;
  setTheme: (theme: Partial<ThemeConfig>) => void;
  setPreset: (presetId: string) => void;
  resetTheme: () => void;
  resolvedMode: 'light' | 'dark'; // Actual mode after resolving 'system'
  exportTheme: () => string; // Export theme as TypeScript config
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function loadThemeFromStorage(): ThemeConfig {
  if (typeof window === 'undefined') return DEFAULT_THEME;

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ThemeConfig;
      // Validate the parsed config has required fields
      if (
        parsed.mode &&
        parsed.baseColor &&
        parsed.themeColor &&
        parsed.radius &&
        parsed.style &&
        parsed.font
      ) {
        return parsed;
      }
    }
  } catch {
    // Invalid stored value, use default
  }

  return DEFAULT_THEME;
}

function saveTheme(theme: ThemeConfig): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // Storage not available
  }
}

function applyThemeToDocument(theme: ThemeConfig, resolvedMode: 'light' | 'dark'): void {
  if (typeof window === 'undefined') return;

  const root = document.documentElement;
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');

  // Apply dark/light class
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedMode);
  root.style.colorScheme = resolvedMode;

  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', resolvedMode === 'dark' ? '#0f172a' : '#f8fafc');
  }

  // Apply base radius (will be scaled by style)
  const baseRadius = RADIUS_VALUES[theme.radius];
  root.style.setProperty('--radius-base', baseRadius);

  // Apply style (spacing and radius scales)
  const style = getStyleById(theme.style);
  if (style) {
    root.style.setProperty('--spacing-scale', style.css.spacingScale.toString());
    root.style.setProperty('--radius-scale', style.css.radiusScale.toString());

    // Calculate actual radius based on base radius and scale
    const radiusNum = parseFloat(baseRadius) || 0.5;
    const scaledRadius = radiusNum * style.css.radiusScale;
    root.style.setProperty('--radius', `${scaledRadius}rem`);
  } else {
    root.style.setProperty('--radius', baseRadius);
    root.style.setProperty('--spacing-scale', '1');
    root.style.setProperty('--radius-scale', '1');
  }

  // Apply base colors
  const baseColors = BASE_COLOR_PALETTES[theme.baseColor][resolvedMode];
  root.style.setProperty('--background', baseColors.background);
  root.style.setProperty('--foreground', baseColors.foreground);
  root.style.setProperty('--card', baseColors.card);
  root.style.setProperty('--card-foreground', baseColors.cardForeground);
  root.style.setProperty('--muted', baseColors.muted);
  root.style.setProperty('--muted-foreground', baseColors.mutedForeground);
  root.style.setProperty('--border', baseColors.border);
  root.style.setProperty('--input', baseColors.input);
  root.style.setProperty('--popover', baseColors.card);
  root.style.setProperty('--popover-foreground', baseColors.cardForeground);

  // Apply theme color (accent/primary)
  const themeColor = getThemeColorById(theme.themeColor);
  if (themeColor) {
    const colorMode = resolvedMode === 'light' ? themeColor.light : themeColor.dark;

    // Special handling for 'neutral' - use base color's foreground as primary
    if (theme.themeColor === 'neutral') {
      root.style.setProperty('--primary', baseColors.foreground);
      root.style.setProperty('--primary-foreground', baseColors.background);
    } else {
      const [h, s, l] = colorMode.primary;
      const [fh, fs, fl] = colorMode.primaryForeground;
      root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
      root.style.setProperty('--primary-foreground', `${fh} ${fs}% ${fl}%`);
    }

    // Secondary and accent use muted colors
    root.style.setProperty('--secondary', baseColors.muted);
    root.style.setProperty('--secondary-foreground', baseColors.foreground);
    root.style.setProperty('--accent', baseColors.muted);
    root.style.setProperty('--accent-foreground', baseColors.foreground);

    // Ring uses primary
    if (theme.themeColor === 'neutral') {
      root.style.setProperty('--ring', baseColors.foreground);
    } else {
      const [h, s, l] = colorMode.primary;
      root.style.setProperty('--ring', `${h} ${s}% ${l}%`);
    }
  }

  // Apply font
  const font = getFontById(theme.font);
  if (font) {
    loadFont(theme.font);
    root.style.setProperty('--font-sans', font.family);
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<ThemeConfig>(loadThemeFromStorage);
  const [resolvedMode, setResolvedMode] = React.useState<'light' | 'dark'>(() =>
    theme.mode === 'system' ? getSystemTheme() : theme.mode
  );

  // Listen for system theme changes
  React.useEffect(() => {
    if (theme.mode !== 'system') {
      setResolvedMode(theme.mode);
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setResolvedMode(e.matches ? 'dark' : 'light');
    };

    setResolvedMode(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme.mode]);

  // Apply theme to document whenever it changes
  React.useEffect(() => {
    applyThemeToDocument(theme, resolvedMode);
  }, [theme, resolvedMode]);

  const setTheme = React.useCallback((updates: Partial<ThemeConfig>) => {
    setThemeState((current) => {
      // If any config option is changed, clear the preset (it's now custom)
      const newTheme: ThemeConfig = {
        ...current,
        ...updates,
        preset: updates.preset !== undefined ? updates.preset : null,
      };
      saveTheme(newTheme);
      return newTheme;
    });
  }, []);

  const setPreset = React.useCallback((presetId: string) => {
    const preset = THEME_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setThemeState((current) => {
        const newTheme: ThemeConfig = {
          ...preset.config,
          mode: current.mode,
          preset: presetId,
        };
        saveTheme(newTheme);
        return newTheme;
      });
    }
  }, []);

  const resetTheme = React.useCallback(() => {
    setThemeState(DEFAULT_THEME);
    saveTheme(DEFAULT_THEME);
  }, []);

  const exportTheme = React.useCallback(() => {
    const config: ThemeConfig = { ...theme };
    const configStr = JSON.stringify(config, null, 2);
    return `/**
 * Default theme configuration.
 * Generated by Theme Panel on ${new Date().toISOString()}
 *
 * To use: Replace the content of apps/web/src/lib/default-theme.ts with this.
 */

import type { ThemeConfig } from '@/contexts/ThemeContext'

export const DEFAULT_THEME: ThemeConfig = ${configStr}
`;
  }, [theme]);

  const value = React.useMemo(
    () => ({
      theme,
      setTheme,
      setPreset,
      resetTheme,
      resolvedMode,
      exportTheme,
    }),
    [theme, setTheme, setPreset, resetTheme, resolvedMode, exportTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export type { ThemeConfig, ColorMode, BaseColor, Radius, Style, ThemeColor, Font };
